import * as THREE from '../extras/three'

import { System } from './System'

import { CSM } from '../libs/csm/CSM'
import { isNumber, isString } from 'lodash-es'

const csmLevels = {
  none: {
    cascades: 1,
    shadowMapSize: 1024,
    castShadow: false,
    lightIntensity: 3,
    // shadowBias: 0.000002,
    // shadowNormalBias: 0.001,
  },
  low: {
    cascades: 1,
    shadowMapSize: 2048,
    castShadow: true,
    lightIntensity: 3,
    shadowBias: 0.0000009,
    shadowNormalBias: 0.001,
  },
  med: {
    cascades: 3,
    shadowMapSize: 1024,
    castShadow: true,
    lightIntensity: 1,
    shadowBias: 0.000002,
    shadowNormalBias: 0.002,
  },
  high: {
    cascades: 3,
    shadowMapSize: 2048,
    castShadow: true,
    lightIntensity: 1,
    shadowBias: 0.000003,
    shadowNormalBias: 0.002,
  },
}

// fix fog distance calc
// see: https://github.com/mrdoob/three.js/issues/14601
// future: https://www.youtube.com/watch?v=k1zGz55EqfU
THREE.ShaderChunk.fog_vertex = `
#ifdef USE_FOG

  // original
  // vFogDepth = - mvPosition.z;

  // radial distance
  vFogDepth = length( mvPosition );

  // cylindrical (ignore altitude)
  // vFogDepth = length( mvPosition.xz );

  // height-based (eg ground fog)
  // vFogDepth = abs( mvPosition.y );

#endif
`

/**
 * Environment System
 *
 * - Runs on the client
 * - Sets up the sky, hdr, sun, shadows, fog etc
 *
 */
const skyVertexShader = `
varying vec3 vPosition;
varying vec2 vUv;
void main() {
  vPosition = position;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

function getCustomUniformType(value) {
  if (typeof value === 'number') return 'float'
  if (Array.isArray(value)) {
    if (value.length === 2) return 'vec2'
    if (value.length === 3) return 'vec3'
    if (value.length === 4) return 'vec4'
  }
  return null
}

function buildCustomUniformDeclarations(userUniforms) {
  if (!userUniforms) return ''
  const declarations = []
  for (const key in userUniforms) {
    const type = getCustomUniformType(userUniforms[key])
    if (!type) continue
    declarations.push(`uniform ${type} ${key};`)
  }
  if (declarations.length === 0) return ''
  return `${declarations.join('\n')}\n`
}

function buildSkyFragmentShader(userCode, userUniforms, userHeader = '') {
  return `
varying vec3 vPosition;
varying vec2 vUv;
uniform float uTime;
uniform vec2 uResolution;
${buildCustomUniformDeclarations(userUniforms)}${userHeader ? userHeader.trim() + '\n' : ''}void main() {
  vec3 direction = normalize(vPosition);
  vec3 color = vec3(0.0);
  float alpha = 1.0;
  ${userCode}
  gl_FragColor = vec4(color, alpha);
}
`
}

function buildShaderUniforms(userUniforms) {
  const uniforms = {
    uTime: { value: 0 },
    uResolution: { value: new THREE.Vector2() },
  }
  if (!userUniforms) return uniforms
  for (const key in userUniforms) {
    const v = userUniforms[key]
    if (typeof v === 'number') {
      uniforms[key] = { value: v }
    } else if (Array.isArray(v)) {
      if (v.length === 2) uniforms[key] = { value: new THREE.Vector2(v[0], v[1]) }
      else if (v.length === 3) uniforms[key] = { value: new THREE.Vector3(v[0], v[1], v[2]) }
      else if (v.length === 4) uniforms[key] = { value: new THREE.Vector4(v[0], v[1], v[2], v[3]) }
    }
  }
  return uniforms
}

export class ClientEnvironment extends System {
  constructor(world) {
    super(world)

    this.model = null
    this.skys = []
    this.sky = null
    this.skyN = 0
    this.bgUrl = null
    this.hdrUrl = null
    this.skyShaderMaterial = null
    this.skyBasicMaterial = null
    this.skyElapsed = 0
  }

  init({ baseEnvironment }) {
    this.base = baseEnvironment
  }

  async start() {
    this.buildCSM()
    this.updateSky()

    this.world.prefs.on('change', this.onPrefsChange)
    this.world.graphics.on('resize', this.onViewportResize)
  }

  addSky(node) {
    const handle = {
      node,
      destroy: () => {
        const idx = this.skys.indexOf(handle)
        if (idx === -1) return
        this.skys.splice(idx, 1)
        this.updateSky()
      },
    }
    this.skys.push(handle)
    this.updateSky()
    return handle
  }

  getSky() {
    // ...
  }

  applyBackgroundSky(bgTexture) {
    if (!this.skyBasicMaterial && this.sky.material?.isMeshBasicMaterial) {
      this.skyBasicMaterial = this.sky.material
    }
    if (this.skyBasicMaterial) {
      this.sky.material = this.skyBasicMaterial
    }
    if (this.skyShaderMaterial) {
      this.skyShaderMaterial.dispose()
      this.skyShaderMaterial = null
    }
    if (bgTexture) {
      bgTexture.minFilter = bgTexture.magFilter = THREE.LinearFilter
      bgTexture.mapping = THREE.EquirectangularReflectionMapping
      bgTexture.colorSpace = THREE.SRGBColorSpace
      this.sky.material.map = bgTexture
      this.sky.visible = true
    } else {
      this.sky.material.map = null
      this.sky.visible = false
    }
  }

  // Live-update the sun WITHOUT a full sky rebuild: keeps the CSM shadow
  // direction and the sky shader's visible sun disc in sync, per frame.
  // `value` is hyperfy's sunDirection convention: the direction the light
  // TRAVELS (toward the scene). The shader negates it to find the disc.
  setSunDirection(value) {
    if (!value) return
    if (this.csm) {
      this.csm.lightDirection = value
    }
    const mat = this.skyShaderMaterial
    if (mat?.uniforms?.uSunDirection) {
      mat.uniforms.uSunDirection.value.copy(value)
    }
  }

  // Live-copy shader uniform VALUES without rebuilding the material.
  // Keys must already be declared (same key set as the last compile).
  setShaderUniforms(values) {
    const mat = this.skyShaderMaterial
    if (!mat?.uniforms || !values) return
    for (const key in values) {
      const u = mat.uniforms[key]
      if (!u) continue
      const v = values[key]
      if (typeof v === 'number') {
        u.value = v
      } else if (v?.isVector2 || v?.isVector3 || v?.isVector4) {
        u.value.copy(v)
      } else if (Array.isArray(v)) {
        if (v.length === 2) u.value.set(v[0], v[1])
        else if (v.length === 3) u.value.set(v[0], v[1], v[2])
        else if (v.length === 4) u.value.set(v[0], v[1], v[2], v[3])
      }
    }
  }

  async updateSky() {
    if (!this.sky) {
      const geometry = new THREE.SphereGeometry(1000, 60, 40)
      const material = new THREE.MeshBasicMaterial({ side: THREE.BackSide })
      this.sky = new THREE.Mesh(geometry, material)
      this.sky.geometry.computeBoundsTree()
      this.sky.material.fog = false
      this.sky.material.toneMapped = false
      this.sky.material.needsUpdate = true
      this.sky.matrixAutoUpdate = false
      this.sky.matrixWorldAutoUpdate = false
      this.sky.visible = false
      this.world.stage.scene.add(this.sky)
    }

    const base = this.base
    const node = this.skys[this.skys.length - 1]?.node
    const shaderCode = node?._shader || null
    const shaderHeader = node?._shaderHeader || null
    const shaderUniforms = node?._shaderUniforms || null
    const bgUrl = node?._bg || base.bg
    const hdrUrl = node?._hdr || base.hdr
    const rotationY = isNumber(node?._rotationY) ? node._rotationY : base.rotationY
    const sunDirection = node?._sunDirection || base.sunDirection
    const sunIntensity = isNumber(node?._sunIntensity) ? node._sunIntensity : base.sunIntensity
    const sunColor = isString(node?._sunColor) ? node._sunColor : base.sunColor
    const fogNear = isNumber(node?._fogNear) ? node._fogNear : base.fogNear
    const fogFar = isNumber(node?._fogFar) ? node._fogFar : base.fogFar
    const fogColor = isString(node?._fogColor) ? node._fogColor : base.fogColor

    const n = ++this.skyN
    let bgTexture
    if (bgUrl) bgTexture = await this.world.loader.load('texture', bgUrl)
    let hdrTexture
    if (hdrUrl) hdrTexture = await this.world.loader.load('hdr', hdrUrl)
    if (n !== this.skyN) return

    if (shaderCode) {
      if (this.skyShaderMaterial) {
        this.skyShaderMaterial.dispose()
        this.skyShaderMaterial = null
      }
      try {
        const uniforms = buildShaderUniforms(shaderUniforms)
        const material = new THREE.ShaderMaterial({
          vertexShader: skyVertexShader,
          fragmentShader: buildSkyFragmentShader(shaderCode, shaderUniforms, shaderHeader),
          uniforms,
          side: THREE.BackSide,
          depthWrite: false,
        })
        material.fog = false
        material.toneMapped = false
        const renderer = this.world.graphics.renderer
        if (!renderer) {
          material.dispose()
          this.applyBackgroundSky(bgTexture)
        } else {
          const prevOnError = renderer.debug.onShaderError
          let compileError = null
          renderer.debug.onShaderError = (gl, program, vs, fs) => {
            const fsLog = gl.getShaderInfoLog(fs)
            const vsLog = gl.getShaderInfoLog(vs)
            compileError = fsLog || vsLog || 'unknown shader error'
          }
          const testScene = new THREE.Scene()
          const testCamera = new THREE.Camera()
          const testMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material)
          testScene.add(testMesh)
          renderer.compile(testScene, testCamera)
          // renderer.compile() builds the program but does NOT trigger the
          // link-status check — that runs on first USE via getUniforms().
          // Force it now so a broken shader is caught here, not at render.
          const props = renderer.properties?.get(material)
          props?.currentProgram?.getUniforms?.()
          renderer.debug.onShaderError = prevOnError
          testMesh.geometry.dispose()
          if (compileError) {
            console.warn('[sky] shader compile error:', compileError)
            material.dispose()
            this.applyBackgroundSky(bgTexture)
          } else {
            if (!this.skyBasicMaterial) {
              this.skyBasicMaterial = this.sky.material
            }
            this.skyShaderMaterial = material
            this.sky.material = this.skyShaderMaterial
            this.sky.visible = true
          }
        }
      } catch (err) {
        console.warn('[sky] shader error:', err)
        this.applyBackgroundSky(bgTexture)
      }
    } else {
      this.applyBackgroundSky(bgTexture)
    }

    if (hdrTexture) {
      // hdrTexture.colorSpace = THREE.NoColorSpace
      // hdrTexture.colorSpace = THREE.SRGBColorSpace
      // hdrTexture.colorSpace = THREE.LinearSRGBColorSpace
      hdrTexture.mapping = THREE.EquirectangularReflectionMapping
      this.world.stage.scene.environment = hdrTexture
    }

    this.world.stage.scene.environmentRotation.y = rotationY
    this.sky.rotation.y = rotationY
    this.sky.matrixWorld.compose(this.sky.position, this.sky.quaternion, this.sky.scale)

    this.csm.lightDirection = sunDirection

    for (const light of this.csm.lights) {
      light.intensity = sunIntensity
      light.color.set(sunColor)
    }

    if (isNumber(fogNear) && isNumber(fogFar) && fogColor) {
      const color = new THREE.Color(fogColor)
      this.world.stage.scene.fog = new THREE.Fog(color, fogNear, fogFar)
    } else {
      this.world.stage.scene.fog = null
    }

    this.skyInfo = {
      bgUrl,
      hdrUrl,
      rotationY,
      sunDirection,
      sunIntensity,
      sunColor,
      fogNear,
      fogFar,
      fogColor,
    }
  }

  update(delta) {
    this.csm.update()
    if (this.skyShaderMaterial) {
      this.skyElapsed += delta
      this.skyShaderMaterial.uniforms.uTime.value = this.skyElapsed
      const renderer = this.world.graphics.renderer
      if (renderer) {
        this.skyShaderMaterial.uniforms.uResolution.value.set(
          renderer.domElement.width,
          renderer.domElement.height
        )
      }
    }
  }

  lateUpdate(delta) {
    this.sky.position.x = this.world.rig.position.x
    this.sky.position.z = this.world.rig.position.z
    this.sky.matrixWorld.setPosition(this.sky.position)
    // this.sky.matrixWorld.copyPosition(this.world.rig.matrixWorld)
  }

  buildCSM() {
    const options = csmLevels[this.world.prefs.shadows]
    if (this.csm) {
      this.csm.updateCascades(options.cascades)
      this.csm.updateShadowMapSize(options.shadowMapSize)
      this.csm.lightDirection = this.skyInfo.sunDirection
      for (const light of this.csm.lights) {
        light.intensity = this.skyInfo.sunIntensity
        light.color.set(this.skyInfo.sunColor)
        light.castShadow = options.castShadow
      }
    } else {
      const scene = this.world.stage.scene
      const camera = this.world.camera
      this.csm = new CSM({
        mode: 'practical', // uniform, logarithmic, practical, custom
        // mode: 'custom',
        // customSplitsCallback: function (cascadeCount, nearDistance, farDistance) {
        //   return [0.05, 0.2, 0.5]
        // },
        cascades: 3,
        maxCascades: 3,
        shadowMapSize: 2048,
        maxFar: 100,
        lightIntensity: Math.PI,
        lightDirection: new THREE.Vector3(0, -1, 0).normalize(),
        fade: true,
        parent: scene,
        camera: camera,
        // note: you can play with bias in console like this:
        // var csm = world.graphics.csm
        // csm.shadowBias = 0.00001
        // csm.shadowNormalBias = 0.002
        // csm.updateFrustums()
        // shadowBias: 0.00001,
        // shadowNormalBias: 0.002,
        // lightNear: 0.0000001,
        // lightFar: 5000,
        // lightMargin: 200,
        // noLastCascadeCutOff: true,
        ...options,
        // note: you can test changes in console and then call csm.updateFrustrums() to debug
      })
      if (!options.castShadow) {
        for (const light of this.csm.lights) {
          light.castShadow = false
        }
      }
    }
  }

  onPrefsChange = changes => {
    if (changes.shadows) {
      this.buildCSM()
      this.updateSky()
    }
  }

  onViewportResize = () => {
    this.csm.updateFrustums()
  }
}
